// Soundboard im Sprachkanal: Sounds für alle abspielen, lokal vorhören, lokale Lautstärke.
import type { ServerDto, SoundDto } from '@lagerfeuer/shared';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { lookupApi, soundsApi } from './api';
import type { ExtraContext } from './index';
import {
  effectiveVolume,
  filterSounds,
  formatSoundDuration,
  playAudio,
  readSoundboardVolume,
  SOUNDBOARD_COOLDOWN_MS,
  soundboardErrorText,
  writeSoundboardVolume,
} from './soundboard-utils';
import { Button, Empty, ErrorBox, Loading } from './ui';
import './soundboard.css';

interface TileProps {
  sound: SoundDto;
  cooling: boolean;
  onPlay: (s: SoundDto) => void;
  onPreview: (s: SoundDto) => void;
}

function SoundTile({ sound, cooling, onPlay, onPreview }: TileProps) {
  return (
    <li className="lfx-sb-tile" data-testid="soundboard-sound" data-sound-id={sound.id}>
      <button
        type="button"
        className={`lfx-sb-play${cooling ? ' lfx-sb-play--cooling' : ''}`}
        data-testid={`soundboard-play-${sound.id}`}
        disabled={cooling}
        aria-label={`${sound.name} für alle abspielen`}
        onClick={() => onPlay(sound)}
      >
        <span className="lfx-sb-emoji" aria-hidden="true">
          {sound.emoji || '🔉'}
        </span>
        <span className="lfx-sb-name">{sound.name}</span>
        <span className="lfx-sb-duration">{formatSoundDuration(sound.durationMs)}</span>
      </button>
      <button
        type="button"
        className="lfx-sb-preview"
        data-testid={`soundboard-preview-${sound.id}`}
        aria-label={`${sound.name} nur für mich vorhören`}
        title="Vorhören (nur für dich)"
        onClick={() => onPreview(sound)}
      >
        ▶
      </button>
    </li>
  );
}

function SoundGrid(props: { sounds: SoundDto[]; testId: string; isCooling: (id: string) => boolean; onPlay: TileProps['onPlay']; onPreview: TileProps['onPreview'] }) {
  return (
    <ul className="lfx-sb-grid" data-testid={props.testId}>
      {props.sounds.map((s) => (
        <SoundTile key={s.id} sound={s} cooling={props.isCooling(s.id)} onPlay={props.onPlay} onPreview={props.onPreview} />
      ))}
    </ul>
  );
}

function OtherServers(props: { serverId: string; query: string; isCooling: (id: string) => boolean; onPlay: TileProps['onPlay']; onPreview: TileProps['onPreview'] }) {
  const servers = useQuery({ queryKey: ['extras', 'lookup', 'servers'], queryFn: lookupApi.servers });
  const others: ServerDto[] = (servers.data ?? []).filter((s) => s.id !== props.serverId);
  const lists = useQueries({
    queries: others.map((s) => ({ queryKey: ['extras', 'sounds', s.id], queryFn: () => soundsApi.list(s.id) })),
  });
  if (servers.isPending) return <Loading testId="soundboard-other-loading" />;
  if (servers.isError) return <ErrorBox error={servers.error} testId="soundboard-other-error" />;
  if (!others.length) return <Empty testId="soundboard-other-empty">Du bist in keinem weiteren Server.</Empty>;
  return (
    <div className="lfx-stack">
      <p className="lfx-muted lfx-sb-hint">Sounds anderer Server brauchen hier das Recht „Externe Sounds verwenden".</p>
      {others.map((srv, i) => {
        const q = lists[i];
        const sounds = filterSounds(q?.data ?? [], props.query);
        return (
          <section key={srv.id} className="lfx-stack lfx-sb-server" data-testid="soundboard-other-server" aria-label={`Sounds aus ${srv.name}`}>
            <h4 className="lfx-sb-server__title">{srv.name}</h4>
            {q?.isPending ? (
              <Loading />
            ) : q?.isError ? (
              <ErrorBox error={q.error} testId="soundboard-other-server-error" />
            ) : sounds.length ? (
              <SoundGrid sounds={sounds} testId={`soundboard-grid-${srv.id}`} isCooling={props.isCooling} onPlay={props.onPlay} onPreview={props.onPreview} />
            ) : (
              <div className="lfx-muted">Keine passenden Sounds.</div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function Soundboard(props: { serverId: string; channelId: string; onClose?: () => void }) {
  const { serverId, channelId } = props;
  const searchId = useId();
  const volumeId = useId();
  const [query, setQuery] = useState('');
  const [volume, setVolume] = useState(readSoundboardVolume);
  const [othersOpen, setOthersOpen] = useState(false);
  const [cooling, setCooling] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const preview = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach(clearTimeout);
      t.clear();
      preview.current?.pause();
    };
  }, []);

  const own = useQuery({ queryKey: ['extras', 'sounds', serverId], queryFn: () => soundsApi.list(serverId) });

  const play = useMutation({
    mutationFn: (s: SoundDto) => soundsApi.play(channelId, s.id),
    onSuccess: () => setError(null),
    onError: (err, s) => setError(soundboardErrorText(err, s.serverId !== serverId)),
  });

  const onPlay = (s: SoundDto) => {
    if (cooling.has(s.id)) return;
    setCooling((cur) => new Set(cur).add(s.id));
    timers.current.set(
      s.id,
      setTimeout(() => {
        timers.current.delete(s.id);
        setCooling((cur) => {
          const next = new Set(cur);
          next.delete(s.id);
          return next;
        });
      }, SOUNDBOARD_COOLDOWN_MS),
    );
    play.mutate(s);
  };

  const onPreview = (s: SoundDto) => {
    preview.current?.pause();
    preview.current = playAudio(s.url, effectiveVolume(s.volume, volume));
  };

  const isCooling = (id: string) => cooling.has(id);
  const sounds = filterSounds(own.data ?? [], query);

  return (
    <section className="lfx-sb" data-testid="soundboard" aria-label="Soundboard">
      <header className="lfx-sb-head">
        <h3 className="lfx-sb-title">Soundboard</h3>
        {props.onClose && (
          <button type="button" className="lfx-icon-btn" aria-label="Soundboard schließen" data-testid="soundboard-close" onClick={props.onClose}>
            ×
          </button>
        )}
      </header>
      <div className="lfx-sb-controls">
        <label className="lfx-sb-sr-only" htmlFor={searchId}>
          Sounds suchen
        </label>
        <input
          id={searchId}
          type="search"
          className="lfx-input lfx-sb-search"
          placeholder="Sounds suchen …"
          data-testid="soundboard-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="lfx-sb-volume">
          <label htmlFor={volumeId} className="lfx-muted">
            Lautstärke
          </label>
          <input
            id={volumeId}
            type="range"
            min={0}
            max={100}
            step={1}
            data-testid="soundboard-volume"
            value={volume}
            aria-valuetext={`${volume} %`}
            onChange={(e) => {
              const v = Number(e.target.value);
              setVolume(v);
              writeSoundboardVolume(v);
            }}
          />
          <span className="lfx-sb-volume__value" data-testid="soundboard-volume-value">
            {volume} %
          </span>
        </div>
      </div>

      {error && (
        <div className="lfx-error" role="alert" data-testid="soundboard-error">
          {error}
        </div>
      )}

      {own.isPending ? (
        <Loading testId="soundboard-loading" />
      ) : own.isError ? (
        <ErrorBox error={own.error} testId="soundboard-load-error" />
      ) : sounds.length ? (
        <SoundGrid sounds={sounds} testId="soundboard-grid" isCooling={isCooling} onPlay={onPlay} onPreview={onPreview} />
      ) : (
        <Empty testId="soundboard-empty">{own.data.length ? 'Keine passenden Sounds.' : 'Dieser Server hat noch keine Sounds.'}</Empty>
      )}

      <div className="lfx-sb-others">
        <Button variant="ghost" testId="soundboard-other-toggle" aria-expanded={othersOpen} onClick={() => setOthersOpen((v) => !v)}>
          {othersOpen ? '▾' : '▸'} Andere Server
        </Button>
        {othersOpen && <OtherServers serverId={serverId} query={query} isCooling={isCooling} onPlay={onPlay} onPreview={onPreview} />}
      </div>
    </section>
  );
}

/** Andockung im Kanal-Header: nur in Sprachkanälen eines Servers. */
export function SoundboardPanel(props: ExtraContext) {
  const { serverId, channelId } = props;
  const channel = useQuery({
    queryKey: ['extras', 'lookup', 'channel', channelId],
    queryFn: () => lookupApi.channel(channelId as string),
    enabled: !!serverId && !!channelId,
  });
  if (!serverId || !channelId) return null;
  if (channel.isPending) return <Loading testId="soundboard-panel-loading" />;
  if (channel.data && channel.data.type !== 'VOICE') {
    return <Empty testId="soundboard-panel-not-voice">Das Soundboard gibt es nur in Sprachkanälen.</Empty>;
  }
  return <Soundboard serverId={serverId} channelId={channelId} onClose={props.onClose} />;
}
